import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, CircularProgress,
  MenuItem, Chip, Tabs, Tab, Divider, InputAdornment,
  Tooltip, useTheme
} from '@mui/material';
import { Add, Edit, Delete, Calculate, Science, Search, ContentCopy } from '@mui/icons-material';
import { Product } from '../../types';
import { getProducts, createProduct, updateProduct, deleteProduct } from '../../api/products';
import api from '../../api/axios';

// ─── Tasarım sabitleri ────────────────────────────────────────────────────────
const BRAND  = '#1A3A6B';
const ACCENT = '#c0392b';

const CABLE_FAMILIES = ['AAC-ACSR', 'Compacted', 'AER', 'NAYY', 'NA2XY', 'NA2XH', 'AMKA PE', 'ABC XLPE', 'ABC HDPE'];

const empty: Partial<Product> = {
  name: '', unit: 'KG', cableFamily: 'AAC-ACSR',
  unitWeight: 0, aluminumFactor: 0, costPrice: 0
};

interface CableParams {
  id: number;
  name: string;
  cableFamily: string;
  insulation: string;
  sheath: string;
  filler: string;
  fazSayisi: number;
  fazKesit: number;
  notrSayisi: number;
  notrKesit: number | null;
  alDolguEtPayi: number;
  hurdaOrani: number;
  iscilik: number;
  enerji: number;
  makara: number;
  refMaliyetPerM: number;
  refAgirlikPerM: number;
  celikBirimFiyat: number;
  bukumOrani: number;
  sokakIzKalinlik: number;
  alasimIzKalinlik: number;
  iletkenHurda: number;
  hurdaIzolasyon: number;
  hurdaFitil: number;
  hurdaDolgu: number;
  hurdaKilaf: number;
  active: boolean;
}

const familyColor: Record<string, string> = {
  'AAC-ACSR': '#1A3A6B', 'Compacted': '#0E7490', 'AER': '#B45309',
  'NAYY': '#D97706', 'NA2XY': '#0284C7', 'NA2XH': '#7C3AED',
  'AMKA PE': '#16A34A', 'ABC XLPE': '#DC2626', 'ABC HDPE': '#9333EA'
};

const INSULATION_TYPES = ['PVC', 'XLPE', 'HFFR', 'PE'];
const SHEATH_TYPES = ['PVC', 'HFFR', 'HDPE', 'PE'];
const FILLER_TYPES = ['PE', 'PVC', 'HFFR'];

const actionBtn = (BORDER: string) => ({
  width: 30, height: 30, borderRadius: '6px',
  border: `1px solid ${BORDER}`, color: '#64748b', bgcolor: '#fff',
});

const ProductsPage: React.FC = () => {
  const theme   = useTheme();
  const BORDER  = theme.palette.divider;
  const SURFACE = theme.palette.background.paper;
  const TEXT2   = theme.palette.text.secondary;

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const pendingCopyParamsRef = React.useRef<any>(null);
  const [form, setForm] = useState<Partial<Product>>(empty);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [selectedFamilies, setSelectedFamilies] = useState<string[]>([]);
  const [cableParams, setCableParams] = useState<CableParams | null>(null);
  const [cableParamsForm, setCableParamsForm] = useState<Partial<CableParams> | null>(null);
  const [loadingParams, setLoadingParams] = useState(false);
  const [calcResult, setCalcResult] = useState<any>(null);
  const [calculating, setCalculating] = useState(false);

  const load = async () => {
    try { setLoading(true); setProducts(await getProducts()); }
    catch { setError('Ürünler yüklenemedi'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filteredProducts = products
      .filter(p => {
        const familyMatch = selectedFamilies.length === 0 || selectedFamilies.includes(p.cableFamily);
        const searchMatch = searchText === '' || p.name.toLowerCase().includes(searchText.toLowerCase());
        return familyMatch && searchMatch;
      })
      .sort((a, b) => {
        const familyOrder = CABLE_FAMILIES.indexOf(a.cableFamily) - CABLE_FAMILIES.indexOf(b.cableFamily);
        if (familyOrder !== 0) return familyOrder;
        return a.name.localeCompare(b.name, 'tr');
      });

  const toggleFamily = (f: string) => setSelectedFamilies(prev =>
      prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);

  const isFormulaFamily = (f: string) =>
      ['NAYY','NA2XY','NA2XH','AER','AMKA PE','ABC XLPE','ABC HDPE','AAC-ACSR','Compacted'].includes(f);
  const isAer = (f: string) => f === 'AER';
  const isAmkaAbc = (f: string) => ['AMKA PE','ABC XLPE','ABC HDPE'].includes(f);

  const handleOpen = async (product?: Product) => {
    setEditing(product || null);
    setForm(product || empty);
    setCableParams(null); setCableParamsForm(null); setCalcResult(null); setTab(0); setOpen(true);
    setError('');
    if (product && isFormulaFamily(product.cableFamily)) {
      setLoadingParams(true);
      try {
        const res = await api.get(`/cost/params/${encodeURIComponent(product.name)}`);
        setCableParams(res.data); setCableParamsForm({ ...res.data });
      } catch {} finally { setLoadingParams(false); }
    }
  };

  const handleSave = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      if (editing) { await updateProduct(editing.id, form); } else { await createProduct(form); }
      if (cableParamsForm) {
        if (cableParams) {
          await api.put(`/cost/params/${cableParams.id}`, { ...cableParamsForm, name: form.name });
          await api.post(`/cost/update-costs/${encodeURIComponent(cableParams.cableFamily)}`);
        } else if (!editing) {
          const paramsToSave = pendingCopyParamsRef.current || cableParamsForm;
          if (paramsToSave) {
            await api.post(`/cost/params`, { ...paramsToSave, name: form.name, id: undefined });
            await api.post(`/cost/update-costs/${encodeURIComponent(form.cableFamily || '')}`);
          }
          pendingCopyParamsRef.current = null;
        }
      }
      setOpen(false); load();
    } catch { setError('Kayıt sırasında hata oluştu'); }
    finally { setSaving(false); }
  };

  const handlePreviewCalc = async () => {
    if (!cableParams || !cableParamsForm) return;
    setCalculating(true);
    try {
      await api.put(`/cost/params/${cableParams.id}`, cableParamsForm);
      const res = await api.get(`/cost/calculate-one/${cableParams.id}`);
      setCalcResult(res.data);
    } catch { setError('Hesaplama hatası'); }
    finally { setCalculating(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Silmek istediğinize emin misiniz?')) return;
    try { await deleteProduct(id); load(); }
    catch { setError('Silme sırasında hata oluştu'); }
  };

  const PreviewBox = () => (
      <Box sx={{ p: 2, bgcolor: '#faf5ff', borderRadius: '8px', border: '1px solid #e9d5ff', mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#7C3AED', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Hesaplama Önizleme</Typography>
          <Button size="small" startIcon={calculating ? <CircularProgress size={14} /> : <Calculate />}
                  onClick={handlePreviewCalc} disabled={calculating} sx={{ color: '#7C3AED' }}>Önizle</Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box><Typography variant="caption" color="text.secondary">Referans Maliyet</Typography>
            <Typography variant="body2" fontWeight="bold">{cableParams?.refMaliyetPerM?.toFixed(6)} $/m</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">Mevcut Maliyet</Typography>
            <Typography variant="body2" fontWeight="bold" color="primary">{form.costPrice?.toFixed(6)} $/m</Typography></Box>
          {calcResult && <Box><Typography variant="caption" color="text.secondary">Önizleme (Güncel)</Typography>
            <Typography variant="body2" fontWeight="bold" color="success.main">{calcResult.maliyetPerM?.toFixed(6)} $/m</Typography></Box>}
        </Box>
      </Box>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // FORMÜL RENDERLERİ — DEĞİŞMEDİ, ORİJİNAL HALİYLE
  // ═══════════════════════════════════════════════════════════════════════════

  const renderCompactedForm = () => (
    <>
      <Alert severity="info" sx={{ mb: 2, borderRadius: '8px', fontSize: '12px', '& .MuiAlert-message': { fontSize: '12px' } }}>
        H ($/m) = (F + Masraf × BirimAğırlık × 0.001) × 0.001 · F = BirimAğırlık × (1+Hurda) × (LME+AlPrim) × 0.001
      </Alert>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#0E7490', mb: 1.25, mt: 0.5 }}>TEMEL PARAMETRELER</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Kesit Alanı"
            value={cableParamsForm!.fazKesit ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, fazKesit: parseFloat(e.target.value) })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">mm²</InputAdornment> } }}
            helperText="B sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Birim Ağırlık"
            value={cableParamsForm!.refAgirlikPerM ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, refAgirlikPerM: parseFloat(e.target.value) })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">kg/m</InputAdornment> } }}
            helperText="C sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Masraf"
            value={cableParamsForm!.alDolguEtPayi ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, alDolguEtPayi: parseFloat(e.target.value) })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">$/ton</InputAdornment> } }}
            helperText="G sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Hurda Oranı"
            value={((cableParamsForm!.hurdaOrani ?? 0) * 100).toFixed(1)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, hurdaOrani: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="E sütunu" />
        </Box>
      </Box>
      <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', mb: 2 }}>
        <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#065f46', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1, display: 'block' }}>Otomatik Hesaplanan</Typography>
        <Box sx={{ display: 'flex', gap: 3, mt: 0.5 }}>
          <Box><Typography variant="caption" color="text.secondary">Birim Ağırlık (kg/km)</Typography>
            <Typography variant="body2" fontWeight="bold">{((cableParamsForm!.refAgirlikPerM ?? 0) * 1000).toFixed(1)}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">Al Faktörü (kg/m)</Typography>
            <Typography variant="body2" fontWeight="bold">{(cableParamsForm!.refAgirlikPerM ?? 0).toFixed(4)}</Typography></Box>
        </Box>
      </Box>
      <PreviewBox />
    </>
  );

  const renderAacForm = () => {
    const C = cableParamsForm!.fazSayisi || 0; const D = cableParamsForm!.fazKesit || 0;
    const E = cableParamsForm!.notrSayisi || 0; const F = (cableParamsForm!.notrKesit as number) || 0;
    const al = C*D*D*Math.PI/4*2.7; const cel = E*F*F*Math.PI/4*7.85;
    const B = (al+cel)*1.02;
    const alOrani = al+cel > 0 ? (al/(al+cel)*100).toFixed(1)+'%' : '100%';
    return (
      <>
        <Alert severity="info" sx={{ mb: 2, borderRadius: '8px', fontSize: '12px', '& .MuiAlert-message': { fontSize: '12px' } }}>
          P ($/m) = O × B × 0.001 · O ($/kg) = (K + Çelik + Masraf) × 0.001 · B = (AlTel + ÇelikTel) × 1.02
        </Alert>
        <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#1A3A6B', mb: 1.25, mt: 0.5 }}>ALÜMİNYUM İLETKEN (C, D)</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
            <TextField size="small" fullWidth type="number" label="Al Tel Sayısı"
              value={cableParamsForm!.fazSayisi ?? ''}
              onChange={(e) => setCableParamsForm({ ...cableParamsForm!, fazSayisi: parseInt(e.target.value) })}
              helperText="C sütunu" />
          </Box>
          <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
            <TextField size="small" fullWidth type="number" label="Al Tel Çapı"
              value={cableParamsForm!.fazKesit ?? ''}
              onChange={(e) => setCableParamsForm({ ...cableParamsForm!, fazKesit: parseFloat(e.target.value) })}
              slotProps={{ input: { endAdornment: <InputAdornment position="end">mm</InputAdornment> } }}
              helperText="D sütunu" />
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#1A3A6B', mb: 1.25, mt: 0.5 }}>ÇELİK TAKVİYE (E, F, G)</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
            <TextField size="small" fullWidth type="number" label="Çelik Tel Sayısı"
              value={cableParamsForm!.notrSayisi ?? ''}
              onChange={(e) => setCableParamsForm({ ...cableParamsForm!, notrSayisi: parseInt(e.target.value) })}
              helperText="E sütunu (0=saf AAC)" />
          </Box>
          <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
            <TextField size="small" fullWidth type="number" label="Çelik Tel Çapı"
              value={cableParamsForm!.notrKesit ?? ''}
              onChange={(e) => setCableParamsForm({ ...cableParamsForm!, notrKesit: parseFloat(e.target.value) || null })}
              slotProps={{ input: { endAdornment: <InputAdornment position="end">mm</InputAdornment> } }}
              helperText="F sütunu" />
          </Box>
          <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
            <TextField size="small" fullWidth type="number" label="Çelik Birim Fiyat"
              value={cableParamsForm!.celikBirimFiyat ?? ''}
              onChange={(e) => setCableParamsForm({ ...cableParamsForm!, celikBirimFiyat: parseFloat(e.target.value) })}
              slotProps={{ input: { endAdornment: <InputAdornment position="end">$/ton</InputAdornment> } }}
              helperText="G sütunu" />
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#1A3A6B', mb: 1.25, mt: 0.5 }}>MALİYET PARAMETRELERİ</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
            <TextField size="small" fullWidth type="number" label="Masraf"
              value={cableParamsForm!.alDolguEtPayi ?? ''}
              onChange={(e) => setCableParamsForm({ ...cableParamsForm!, alDolguEtPayi: parseFloat(e.target.value) })}
              slotProps={{ input: { endAdornment: <InputAdornment position="end">$/ton</InputAdornment> } }}
              helperText="N sütunu" />
          </Box>
        </Box>
        <Box sx={{ p: 2, bgcolor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe', mb: 2 }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#1e40af', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1, display: 'block' }}>Otomatik Hesaplanan</Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 0.5 }}>
            <Box><Typography variant="caption" color="text.secondary">Birim Ağırlık (kg/km)</Typography>
              <Typography variant="body2" fontWeight="bold">{B.toFixed(3)}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Alüm. Oranı</Typography>
              <Typography variant="body2" fontWeight="bold">{alOrani}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Çelik Oranı</Typography>
              <Typography variant="body2" fontWeight="bold">
                {al+cel > 0 ? (cel/(al+cel)*100).toFixed(1)+'%' : '0%'}
              </Typography></Box>
            {calcResult && calcResult.agirlikPerM > 0 && <>
              <Box><Typography variant="caption" color="text.secondary">Al Maliyet ($/ton)</Typography>
                <Typography variant="body2" fontWeight="bold" color="#1565c0">
                  {calcResult.bilesimler ? (calcResult.bilesimler[0] / calcResult.agirlikPerM).toFixed(2) : '—'}
                </Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Çelik Maliyet ($/ton)</Typography>
                <Typography variant="body2" fontWeight="bold" color="#5c3317">
                  {calcResult.bilesimler ? (calcResult.bilesimler[1] / calcResult.agirlikPerM).toFixed(2) : '—'}
                </Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Masraf ($/ton)</Typography>
                <Typography variant="body2" fontWeight="bold" color="#616161">
                  {calcResult.bilesimler ? (calcResult.bilesimler[2] / calcResult.agirlikPerM).toFixed(2) : '—'}
                </Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Toplam ($/kg)</Typography>
                <Typography variant="body2" fontWeight="bold" color="success.main">
                  {(calcResult.maliyetPerM / calcResult.agirlikPerM).toFixed(6)}
                </Typography></Box>
            </>}
          </Box>
        </Box>
        <PreviewBox />
      </>
    );
  };

  const renderAerForm = () => (
    <>
      <Alert severity="info" sx={{ mb: 2, borderRadius: '8px', fontSize: '12px', '& .MuiAlert-message': { fontSize: '12px' } }}>
        AO ($/m) = (N + Y + AF + AJ + AM) × 0.001
      </Alert>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#B45309', mb: 1.25, mt: 0.5 }}>FAZ İLETKENİ (B, C)</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Faz Sayısı"
            value={cableParamsForm!.fazSayisi ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, fazSayisi: parseInt(e.target.value) })}
            helperText="B sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Faz Kesiti"
            value={cableParamsForm!.fazKesit ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, fazKesit: parseFloat(e.target.value) })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">mm²</InputAdornment> } }}
            helperText="C sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Faz İz. Kalınlığı"
            value={cableParamsForm!.hurdaOrani ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, hurdaOrani: parseFloat(e.target.value) })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">mm</InputAdornment> } }}
            helperText="P sütunu" />
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#B45309', mb: 1.25, mt: 0.5 }}>SOKAK FAZ (D, E) — opsiyonel</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Sokak Faz Sayısı"
            value={cableParamsForm!.notrSayisi ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, notrSayisi: parseInt(e.target.value) })}
            helperText="D sütunu (0=yok)" />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Sokak Faz Kesiti"
            value={cableParamsForm!.notrKesit ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, notrKesit: parseFloat(e.target.value) || null })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">mm²</InputAdornment> } }}
            helperText="E sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Sokak İz. Kalınlığı"
            value={cableParamsForm!.sokakIzKalinlik ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, sokakIzKalinlik: parseFloat(e.target.value) })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">mm</InputAdornment> } }}
            helperText="R sütunu" />
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#B45309', mb: 1.25, mt: 0.5 }}>AŞKIM TELİ & BÜKÜM (F, K)</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Askı Teli Kesiti"
            value={cableParamsForm!.alDolguEtPayi ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, alDolguEtPayi: parseFloat(e.target.value) || 0 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">mm²</InputAdornment> } }}
            helperText="F sütunu (0=yok)" />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Büküm Oranı"
            value={((cableParamsForm!.bukumOrani ?? 0) * 100).toFixed(1)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, bukumOrani: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="K sütunu" />
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Divider sx={{ my: 2 }} />
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#B45309', mb: 1.25, mt: 0.5 }}>HURDA ORANLARI</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 30%', minWidth: 120 }}>
          <TextField size="small" fullWidth type="number" label="İletken Hurda"
            value={((cableParamsForm!.iletkenHurda ?? 0.01) * 100).toFixed(2)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, iletkenHurda: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="M sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 30%', minWidth: 120 }}>
          <TextField size="small" fullWidth type="number" label="İzolasyon Hurda"
            value={((cableParamsForm!.hurdaIzolasyon ?? 0.03) * 100).toFixed(2)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, hurdaIzolasyon: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="X sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 30%', minWidth: 120 }}>
          <TextField size="small" fullWidth type="number" label="Alaşım Hurda"
            value={((cableParamsForm!.hurdaFitil ?? 0.01) * 100).toFixed(2)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, hurdaFitil: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="AE sütunu" />
        </Box>
      </Box>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#B45309', mb: 1.25, mt: 0.5 }}>İŞLETME MALİYETLERİ (AH, AP, AL)</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 30%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Enerji+İşçilik"
            value={((cableParamsForm!.iscilik ?? 0) * 100).toFixed(3)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, iscilik: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="AH sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 30%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Alaşım Enerji+İşçilik"
            value={((cableParamsForm!.enerji ?? 0) * 100).toFixed(3)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, enerji: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="AP sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 30%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Makara"
            value={((cableParamsForm!.makara ?? 0) * 100).toFixed(3)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, makara: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="AL sütunu" />
        </Box>
      </Box>
      <PreviewBox />
    </>
  );

  const renderAmkaAbcForm = () => (
    <>
      <Alert severity="info" sx={{ mb: 2, borderRadius: '8px', fontSize: '12px', '& .MuiAlert-message': { fontSize: '12px' } }}>
        AV ($/m) = (O + AM + W + AQ + AT) × 0.001 · O=Al iletken · AM=İzolasyon · W=Alaşım teli
      </Alert>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#16A34A', mb: 1.25, mt: 0.5 }}>FAZ İLETKENİ (B, C)</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Faz Sayısı"
            value={cableParamsForm!.fazSayisi ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, fazSayisi: parseInt(e.target.value) })}
            helperText="B sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Faz Kesiti"
            value={cableParamsForm!.fazKesit ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, fazKesit: parseFloat(e.target.value) })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">mm²</InputAdornment> } }}
            helperText="C sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Faz İz. Kalınlığı"
            value={cableParamsForm!.hurdaOrani ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, hurdaOrani: parseFloat(e.target.value) })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">mm</InputAdornment> } }}
            helperText="Y sütunu" />
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#16A34A', mb: 1.25, mt: 0.5 }}>SOKAK FAZ (D, E) — opsiyonel</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Sokak Faz Sayısı"
            value={cableParamsForm!.notrSayisi ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, notrSayisi: parseInt(e.target.value) })}
            helperText="D sütunu (0=yok)" />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Sokak Faz Kesiti"
            value={cableParamsForm!.notrKesit ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, notrKesit: parseFloat(e.target.value) || null })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">mm²</InputAdornment> } }}
            helperText="E sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Sokak İz. Kalınlığı"
            value={cableParamsForm!.sokakIzKalinlik ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, sokakIzKalinlik: parseFloat(e.target.value) })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">mm</InputAdornment> } }}
            helperText="Z sütunu (0=yok)" />
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#16A34A', mb: 1.25, mt: 0.5 }}>ALAŞIM TELİ (F, AA, K)</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Alaşım Teli Kesiti"
            value={cableParamsForm!.alDolguEtPayi ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, alDolguEtPayi: parseFloat(e.target.value) || 0 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">mm²</InputAdornment> } }}
            helperText="F sütunu (0=yok)" />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Alaşım İz. Kalınlığı"
            value={cableParamsForm!.alasimIzKalinlik ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, alasimIzKalinlik: parseFloat(e.target.value) })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">mm</InputAdornment> } }}
            helperText="AA sütunu (0=çıplak tel)" />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Büküm Oranı"
            value={((cableParamsForm!.bukumOrani ?? 0) * 100).toFixed(1)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, bukumOrani: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="K sütunu" />
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Divider sx={{ my: 2 }} />
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#16A34A', mb: 1.25, mt: 0.5 }}>HURDA ORANLARI</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 30%', minWidth: 120 }}>
          <TextField size="small" fullWidth type="number" label="İletken Hurda"
            value={((cableParamsForm!.iletkenHurda ?? 0.01) * 100).toFixed(2)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, iletkenHurda: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="N sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 30%', minWidth: 120 }}>
          <TextField size="small" fullWidth type="number" label="İzolasyon Hurda"
            value={((cableParamsForm!.hurdaIzolasyon ?? 0.03) * 100).toFixed(2)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, hurdaIzolasyon: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="AL sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 30%', minWidth: 120 }}>
          <TextField size="small" fullWidth type="number" label="Alaşım Hurda"
            value={((cableParamsForm!.hurdaFitil ?? 0.01) * 100).toFixed(2)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, hurdaFitil: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="V sütunu" />
        </Box>
      </Box>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#16A34A', mb: 1.25, mt: 0.5 }}>İŞLETME MALİYETLERİ (AO, AP, AS)</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 30%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Enerji+İşçilik"
            value={((cableParamsForm!.iscilik ?? 0) * 100).toFixed(3)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, iscilik: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="AO sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 30%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Alaşım Enerji+İşçilik"
            value={((cableParamsForm!.enerji ?? 0) * 100).toFixed(3)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, enerji: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="AP sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 30%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Makara"
            value={((cableParamsForm!.makara ?? 0) * 100).toFixed(3)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, makara: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="AS sütunu" />
        </Box>
      </Box>
      <PreviewBox />
    </>
  );

  const renderNayyForm = () => (
    <>
      <Alert severity="info" sx={{ mb: 2, borderRadius: '8px', fontSize: '12px', '& .MuiAlert-message': { fontSize: '12px' } }}>
        BN ($/m) = (İletken + İzolasyon + Dolgu + Dış Kılıf) × (1 + İşçilik + Enerji + Makara) / 1000
      </Alert>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#1A3A6B', mb: 1.25, mt: 0.5 }}>İLETKEN YAPISI</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Faz Sayısı"
            value={cableParamsForm!.fazSayisi ?? ''} helperText="1-5"
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, fazSayisi: parseInt(e.target.value) })} />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Faz Kesiti"
            value={cableParamsForm!.fazKesit ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, fazKesit: parseFloat(e.target.value) })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">mm²</InputAdornment> } }} />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Nötr Sayısı"
            value={cableParamsForm!.notrSayisi ?? ''} helperText="0=yok"
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, notrSayisi: parseInt(e.target.value) })} />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Nötr Kesiti"
            value={cableParamsForm!.notrKesit ?? ''} helperText="boş=yok"
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, notrKesit: parseFloat(e.target.value) || null })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">mm²</InputAdornment> } }} />
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#1A3A6B', mb: 1.25, mt: 0.5 }}>MALZEME TİPLERİ</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 30%', minWidth: 140 }}>
          <TextField select size="small" fullWidth label="İzolasyon"
            value={cableParamsForm!.insulation || 'PVC'}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, insulation: e.target.value })}>
            {INSULATION_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
        </Box>
        <Box sx={{ flex: '1 1 30%', minWidth: 140 }}>
          <TextField select size="small" fullWidth label="Dış Kılıf"
            value={cableParamsForm!.sheath || 'PVC'}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, sheath: e.target.value })}>
            {SHEATH_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
        </Box>
        <Box sx={{ flex: '1 1 30%', minWidth: 140 }}>
          <TextField select size="small" fullWidth label="Dolgu"
            value={cableParamsForm!.filler || 'PE'}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, filler: e.target.value })}>
            {FILLER_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#1A3A6B', mb: 1.25, mt: 0.5 }}>DÜZELTME FAKTÖRLERİ</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="AL Dolgu Et Payı"
            value={cableParamsForm!.alDolguEtPayi ?? ''}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, alDolguEtPayi: parseFloat(e.target.value) })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">mm</InputAdornment> } }}
            helperText="AL sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 22%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Hurda Oranı"
            value={((cableParamsForm!.hurdaOrani ?? 0) * 100).toFixed(1)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, hurdaOrani: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="genellikle %1" />
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Divider sx={{ my: 2 }} />
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#1A3A6B', mb: 1.25, mt: 0.5 }}>HURDA ORANLARI</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 18%', minWidth: 120 }}>
          <TextField size="small" fullWidth type="number" label="İletken Hurda"
            value={((cableParamsForm!.iletkenHurda ?? 0.01) * 100).toFixed(2)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, iletkenHurda: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="N sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 18%', minWidth: 120 }}>
          <TextField size="small" fullWidth type="number" label="İzolasyon Hurda"
            value={((cableParamsForm!.hurdaIzolasyon ?? 0.03) * 100).toFixed(2)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, hurdaIzolasyon: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="AA sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 18%', minWidth: 120 }}>
          <TextField size="small" fullWidth type="number" label="Fitil Hurda"
            value={((cableParamsForm!.hurdaFitil ?? 0.03) * 100).toFixed(2)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, hurdaFitil: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="AH sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 18%', minWidth: 120 }}>
          <TextField size="small" fullWidth type="number" label="Dolgu Hurda"
            value={((cableParamsForm!.hurdaDolgu ?? 0.03) * 100).toFixed(2)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, hurdaDolgu: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="AX sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 18%', minWidth: 120 }}>
          <TextField size="small" fullWidth type="number" label="Kılıf Hurda"
            value={((cableParamsForm!.hurdaKilaf ?? 0.03) * 100).toFixed(2)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, hurdaKilaf: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="BH sütunu" />
        </Box>
      </Box>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#1A3A6B', mb: 1.25, mt: 0.5 }}>İŞLETME MALİYETLERİ</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: '1 1 30%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="İşçilik"
            value={((cableParamsForm!.iscilik ?? 0) * 100).toFixed(4)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, iscilik: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="BK sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 30%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Enerji"
            value={((cableParamsForm!.enerji ?? 0) * 100).toFixed(4)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, enerji: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="BL sütunu" />
        </Box>
        <Box sx={{ flex: '1 1 30%', minWidth: 140 }}>
          <TextField size="small" fullWidth type="number" label="Makara"
            value={((cableParamsForm!.makara ?? 0) * 100).toFixed(4)}
            onChange={(e) => setCableParamsForm({ ...cableParamsForm!, makara: parseFloat(e.target.value) / 100 })}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
            helperText="BM sütunu" />
        </Box>
      </Box>
      <PreviewBox />
    </>
  );


  // ═══════════════════════════════════════════════════════════════════════════
  // ANA RENDER — Yeni tasarım sistemi
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ p: 3, bgcolor: theme.palette.background.default, minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
            Ürünler
          </Typography>
          <Typography sx={{ fontSize: '13px', color: TEXT2, mt: 0.5 }}>
            <Box component="span" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>{filteredProducts.length}</Box> ürün
            {filteredProducts.length !== products.length && (
              <Box component="span" sx={{ color: TEXT2 }}> / {products.length} toplam</Box>
            )}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}
                sx={{
                  bgcolor: ACCENT, fontWeight: 600, borderRadius: '8px',
                  px: 2.5, fontSize: '13px', boxShadow: 'none', height: 38,
                  '&:hover': { bgcolor: '#a93226', boxShadow: 'none' },
                }}>
          Yeni Ürün
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Filter paneli ── */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: SURFACE }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
              placeholder="Ürün adı ara..." value={searchText}
              onChange={(e) => setSearchText(e.target.value)} size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: TEXT2 }} /></InputAdornment>,
                sx: { borderRadius: '8px', fontSize: '13px', minWidth: 220 },
              }}
              sx={{ '& fieldset': { borderColor: BORDER } }} />

          <Box sx={{ width: 1, height: 24, bgcolor: BORDER, mx: 0.5 }} />

          <Typography sx={{ fontSize: '12px', color: TEXT2, fontWeight: 600 }}>Aile:</Typography>
          {CABLE_FAMILIES.map(family => {
            const count = products.filter(p => p.cableFamily === family).length;
            if (count === 0) return null;
            const isSelected = selectedFamilies.includes(family);
            return (
                <Chip key={family} label={`${family} (${count})`} size="small"
                      onClick={() => toggleFamily(family)}
                      sx={{
                        height: 22, fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                        bgcolor: isSelected ? familyColor[family] || '#64748B' : 'transparent',
                        color: isSelected ? 'white' : familyColor[family] || '#64748B',
                        border: `1.5px solid ${familyColor[family] || '#64748B'}`,
                        '&:hover': { bgcolor: familyColor[family] || '#64748B', color: 'white', opacity: 0.85 }
                      }} />
            );
          })}

          {selectedFamilies.length > 0 && (
              <Chip label="Temizle" size="small" onClick={() => setSelectedFamilies([])}
                    sx={{ height: 22, fontSize: '11px', bgcolor: '#f1f5f9', color: '#64748B', cursor: 'pointer' }} />
          )}
        </Box>
      </Paper>

      {/* ── Tablo ── */}
      {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress sx={{ color: BRAND }} />
          </Box>
      ) : (
          <TableContainer component={Paper} elevation={0}
                          sx={{ borderRadius: '10px', border: `1px solid ${BORDER}`, overflow: 'hidden', bgcolor: SURFACE }}>
            <Table>
              <TableHead>
                <TableRow>
                  {['Ürün Adı','Kablo Ailesi','Birim','Birim Ağırlık','Al Faktörü','Maliyet ($)','İşlem'].map((h, idx) => (
                      <TableCell key={h} align={['Birim Ağırlık','Al Faktörü','Maliyet ($)'].includes(h) ? 'right' : (h === 'İşlem' ? 'center' : 'left')}
                                 style={{ background: '#f8f9fc', paddingTop: 12, paddingBottom: 12,
                                   fontSize: 11, fontWeight: 600, color: '#8a92a6',
                                   textTransform: 'uppercase', letterSpacing: '0.05em',
                                   borderBottom: '1px solid #e8eaf0' }}>
                        {h}
                      </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8, color: TEXT2 }}>
                        <Typography sx={{ fontSize: '14px' }}>
                          {products.length === 0 ? 'Henüz ürün eklenmemiş' : 'Filtreyle eşleşen ürün bulunamadı'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                ) : (
                    filteredProducts.map((p) => (
                        <TableRow key={p.id}
                                  sx={{
                                    transition: 'background .1s',
                                    '& td': { borderBottom: '1px solid #e8eaf0', py: 0 },
                                    '&:hover td': { bgcolor: '#fafbfe !important' },
                                  }}>
                          <TableCell sx={{ height: 56 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: theme.palette.text.primary }}>
                                {p.name}
                              </Typography>
                              {isFormulaFamily(p.cableFamily) && (
                                  <Tooltip title="Formül parametreleri düzenlenebilir">
                                    <Science sx={{ fontSize: 13, color: '#7C3AED', opacity: 0.7 }} />
                                  </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={p.cableFamily} size="small"
                                  sx={{
                                    bgcolor: familyColor[p.cableFamily] || '#64748B', color: 'white',
                                    fontWeight: 700, fontSize: '11px', height: 22, borderRadius: '6px'
                                  }} />
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '12px', color: TEXT2 }}>{p.unit}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontSize: '12px', fontFamily: 'monospace' }}>{p.unitWeight?.toFixed(3)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontSize: '12px', fontFamily: 'monospace' }}>{p.aluminumFactor?.toFixed(3)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontSize: '12px', fontFamily: 'monospace', fontWeight: 600 }}>{p.costPrice?.toFixed(3)}</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ pr: 2 }}>
                            <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <Tooltip title="Düzenle">
                                <IconButton size="small" onClick={() => handleOpen(p)}
                                            style={actionBtn(BORDER)}
                                            sx={{ '&:hover': { background: '#fff8ed !important', color: '#d97706 !important', borderColor: '#fed7aa !important' } }}>
                                  <Edit sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Kopyala">
                                <IconButton size="small"
                                            style={actionBtn(BORDER)}
                                            sx={{ '&:hover': { background: '#f0fdf4 !important', color: '#16A34A !important', borderColor: '#86efac !important' } }}
                                            onClick={async () => {
                                              setEditing(null);
                                              setForm({ ...p, id: undefined as any, name: p.name + '-Copy' });
                                              setCableParams(null); setCableParamsForm(null); setCalcResult(null); setTab(0); setOpen(true);
                                              if (isFormulaFamily(p.cableFamily)) {
                                                setLoadingParams(true);
                                                try {
                                                  const res = await api.get(`/cost/params/${encodeURIComponent(p.name)}`);
                                                  setCableParams(null);
                                                  setCableParamsForm({ ...res.data, id: undefined });
                                                  pendingCopyParamsRef.current = { ...res.data, id: undefined };
                                                } catch {} finally { setLoadingParams(false); }
                                              }
                                            }}>
                                  <ContentCopy sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Sil">
                                <IconButton size="small" onClick={() => handleDelete(p.id)}
                                            style={actionBtn(BORDER)}
                                            sx={{ '&:hover': { background: '#fff0f0 !important', color: '#dc2626 !important', borderColor: '#fca5a5 !important' } }}>
                                  <Delete sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
      )}

      {/* ── Dialog ── */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth
              PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
        <DialogTitle sx={{ bgcolor: BRAND, color: '#fff', py: 2, px: 3, fontSize: '15px', fontWeight: 600 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Science fontSize="small" />
            {editing ? 'Ürün Düzenle' : 'Yeni Ürün'}
            {cableParams && (
                <Chip label="Formül Parametreleri Mevcut" size="small"
                      sx={{ bgcolor: '#7C3AED', color: 'white', ml: 1, height: 22, fontSize: '11px', fontWeight: 600 }} />
            )}
          </Box>
        </DialogTitle>
        {cableParams && (
            <Tabs value={tab} onChange={(_, v) => setTab(v)}
                  sx={{ px: 3, borderBottom: `1px solid ${BORDER}`,
                    '& .Mui-selected': { color: '#7C3AED' },
                    '& .MuiTabs-indicator': { bgcolor: '#7C3AED' } }}>
              <Tab label="Temel Bilgiler" sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600 }} />
              <Tab label="Formül Parametreleri" sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600 }} />
            </Tabs>
        )}
        <DialogContent sx={{ px: 3, pb: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
          {loadingParams ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>
          ) : (
              <>
                {(!cableParams || tab === 0) && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                      <TextField label="Ürün Adı *" value={form.name || ''}
                                 onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth size="small"
                                 sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                      <TextField select label="Kablo Ailesi" value={form.cableFamily || 'AAC-ACSR'}
                                 onChange={(e) => setForm({ ...form, cableFamily: e.target.value })} fullWidth size="small"
                                 sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                        {CABLE_FAMILIES.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                      </TextField>
                      <TextField select label="Birim" value={form.unit || 'KG'}
                                 onChange={(e) => setForm({ ...form, unit: e.target.value })} fullWidth size="small"
                                 sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                        <MenuItem value="KG">KG</MenuItem>
                        <MenuItem value="M">M</MenuItem>
                      </TextField>
                      <TextField label="Birim Ağırlık" type="number" value={form.unitWeight ?? 0}
                                 onChange={(e) => setForm({ ...form, unitWeight: parseFloat(e.target.value) })}
                                 fullWidth helperText="kg/m" size="small"
                                 sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                      <TextField label="Alüminyum Faktörü" type="number" value={form.aluminumFactor ?? 0}
                                 onChange={(e) => setForm({ ...form, aluminumFactor: parseFloat(e.target.value) })}
                                 fullWidth helperText="kg/m (formül ailelerinde otomatik hesaplanır)" size="small"
                                 sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                      <TextField label="Maliyet Fiyatı ($)" type="number" value={form.costPrice ?? 0}
                                 onChange={(e) => setForm({ ...form, costPrice: parseFloat(e.target.value) })}
                                 fullWidth helperText="$/m veya $/kg (formül ailelerinde otomatik güncellenir)" size="small"
                                 sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                    </Box>
                )}
                {cableParams && tab === 1 && cableParamsForm && (
                    <Box sx={{ mt: 1 }}>
                      {form.cableFamily === 'Compacted' ? renderCompactedForm()
                          : form.cableFamily === 'AAC-ACSR' ? renderAacForm()
                              : isAer(form.cableFamily || '') ? renderAerForm()
                                  : isAmkaAbc(form.cableFamily || '') ? renderAmkaAbcForm()
                                      : renderNayyForm()
                      }
                    </Box>
                )}
              </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setOpen(false)} sx={{ borderRadius: '8px', color: TEXT2, textTransform: 'none' }}>
            İptal
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name}
                  sx={cableParams ? {
                    bgcolor: '#7C3AED', borderRadius: '8px', fontWeight: 600, textTransform: 'none', px: 3, boxShadow: 'none',
                    '&:hover': { bgcolor: '#6D28D9', boxShadow: 'none' },
                  } : {
                    bgcolor: BRAND, borderRadius: '8px', fontWeight: 600, textTransform: 'none', px: 3, boxShadow: 'none',
                    '&:hover': { bgcolor: '#0f2a52', boxShadow: 'none' },
                  }}>
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductsPage;
